import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import PageHeader from '../../components/common/PageHeader';
import Pagination from '../../components/common/Pagination';
import { formatDate } from '../../utils/dateFormat';
import styles from './Entries.module.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Safely convert any value to a display string.
 * Handles primitives, objects, arrays, null, undefined.
 */
function toDisplayValue(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function parseEntryData(data_decoded) {
  if (!data_decoded) return { isGrid: false, fieldNames: [], rows: [] };
  // Grid format: {type: "grid", template: {...}, data: {...}}
  if (data_decoded.type === 'grid' && data_decoded.template && data_decoded.data) {
    return {
      isGrid: true,
      fieldNames: [],
      rows: [],
      gridTemplate: data_decoded.template,
      gridData: data_decoded.data,
    };
  }
  // Top-level array (non-standard but guard anyway)
  if (Array.isArray(data_decoded)) {
    const rows = data_decoded.filter(r => r && typeof r === 'object');
    const fieldNames = rows.length > 0 ? Object.keys(rows[0]) : [];
    return { isGrid: false, fieldNames, rows };
  }
  // Standard formats: {rows: [...]} or flat {field: value, ...}
  const rows = Array.isArray(data_decoded.rows)
    ? data_decoded.rows.filter(r => r && typeof r === 'object')
    : [data_decoded];
  const fieldNames = rows.length > 0 ? Object.keys(rows[0]) : [];
  return { isGrid: false, fieldNames, rows };
}

// ── Grid data display (accordion) ────────────────────────────────────────────

function GridDataTable({ template, gridData }) {
  if (!template) return <p className={styles.noDataText}>No template data available.</p>;
  const { columns = [], rows = [], computed_columns = [] } = template;
  const hasTotal = computed_columns.some(cc => cc.formula === 'sum');

  // Derive column labels — fall back to data keys or ordinal index
  function colLabel(col, idx) {
    return (col.label || col.name || '').trim() || `Col ${idx + 1}`;
  }

  // Derive row labels — fall back to data keys or ordinal index
  function rowLabel(row, idx) {
    return (row.label || row.name || '').trim() || `Row ${idx + 1}`;
  }

  function getRowTotal(rowId, rowType) {
    const vals = columns.map(col => parseFloat(gridData?.[rowId]?.[col.id]) || 0);
    const sum = vals.reduce((a, b) => a + b, 0);
    if (rowType === 'currency') return `$${sum.toFixed(2)}`;
    return sum % 1 === 0 ? String(sum) : sum.toFixed(2);
  }

  function fmtCell(val, rowType) {
    if (val === '' || val === undefined || val === null) return '—';
    if (rowType === 'currency') return `$${parseFloat(val).toFixed(2)}`;
    return String(val);
  }

  return (
    <div className={styles.gridAccordionWrap}>
      <table className={styles.gridAccordionTable}>
        <thead>
          <tr>
            <th className={styles.gridAccordionCorner}>Row / Column</th>
            {columns.map((col, idx) => (
              <th key={col.id || idx} className={styles.gridAccordionColHead}>
                {colLabel(col, idx)}
              </th>
            ))}
            {hasTotal && <th className={styles.gridAccordionTotalHead}>TOTAL</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row.id || idx} className={styles.gridAccordionDataRow}>
              <td className={styles.gridAccordionRowLabel}>{rowLabel(row, idx)}</td>
              {columns.map((col, cIdx) => (
                <td key={col.id || cIdx} className={styles.gridAccordionCell}>
                  {fmtCell(gridData?.[row.id]?.[col.id], row.type)}
                </td>
              ))}
              {hasTotal && (
                <td className={styles.gridAccordionTotalCell}>
                  {getRowTotal(row.id, row.type)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Amendment history modal ───────────────────────────────────────────────────

function AmendmentPanel({ entryId, onClose }) {
  const [amendments, setAmendments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      try {
        const res = await api.get(`/entries/${entryId}/amendments/`);
        if (res.data.success) setAmendments(res.data.data || []);
      } catch { /* empty */ }
      setLoading(false);
    }
    fetch();
  }, [entryId]);

  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>Amendment History</h2>
          <button onClick={onClose} className={styles.closeBtn}>&times;</button>
        </div>
        {loading ? <p>Loading...</p> : amendments.length === 0 ? (
          <p className={styles.emptyText}>No amendments for this entry.</p>
        ) : (
          <div className={styles.amendmentList}>
            {amendments.map(a => (
              <div key={a.id} className={styles.amendmentItem}>
                <div className={styles.amendmentHeader}>
                  <span className={styles.fieldName}>Field: {a.field_id}</span>
                  <span className={styles.amendmentDate}>
                    {new Date(a.amended_at).toLocaleString()}
                  </span>
                </div>
                <div className={styles.amendmentValues}>
                  <div className={styles.valueBox}>
                    <span className={styles.valueLabel}>Original</span>
                    <span className={styles.valueText}>{toDisplayValue(a.original_value_decoded)}</span>
                  </div>
                  <span className={styles.arrow}>&rarr;</span>
                  <div className={styles.valueBox}>
                    <span className={styles.valueLabel}>Corrected</span>
                    <span className={styles.valueText}>{toDisplayValue(a.corrected_value_decoded)}</span>
                  </div>
                </div>
                <div className={styles.reason}>
                  <strong>Reason:</strong> {toDisplayValue(a.reason_decoded)}
                </div>
                <div className={styles.amendedBy}>
                  By: {a.amended_by_email || 'Owner'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Amend entry modal ─────────────────────────────────────────────────────────

function AmendForm({ entryId, entryData, onSave, onCancel }) {
  const [rows, setRows] = useState([{ fieldId: '', correctedValue: '' }]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Build field options — grid entries use "row_id.col_id" keys with human labels
  const isGridEntry = entryData?.type === 'grid';
  let fields = [];
  if (isGridEntry) {
    const tpl = entryData.template || {};
    const gd = entryData.data || {};
    (tpl.rows || []).forEach(row => {
      (tpl.columns || []).forEach(col => {
        const fieldId = `${row.id}.${col.id}`;
        const val = gd[row.id]?.[col.id];
        fields.push([fieldId, `${row.label} — ${col.label}`, val]);
      });
    });
  } else {
    const { fieldNames, rows: dataRows } = parseEntryData(entryData);
    const firstRow = dataRows[0] || {};
    fields = fieldNames.map(name => [name, name, firstRow[name]]);
  }

  function updateRow(idx, key, value) {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [key]: value } : r));
  }

  function addRow() {
    setRows(prev => [...prev, { fieldId: '', correctedValue: '' }]);
  }

  function removeRow(idx) {
    setRows(prev => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (rows.some(r => !r.fieldId || !r.correctedValue.trim())) {
      setError('All fields and corrected values are required.');
      return;
    }
    if (!reason.trim()) {
      setError('Amendment reason is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      for (const row of rows) {
        await api.post(`/entries/${entryId}/amend/`, {
          field_id: row.fieldId,
          corrected_value: row.correctedValue,
          reason,
        });
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create amendment.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <h2>Amend Entry</h2>
        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.amendRowsHeader}>
            <span className={styles.amendRowsHeaderLabel}>
              Field to Amend <span className={styles.required}>*</span>
            </span>
            <span className={styles.amendRowsHeaderLabel}>
              Corrected Value <span className={styles.required}>*</span>
            </span>
            <span />
          </div>

          <div className={styles.amendRowList}>
            {rows.map((row, idx) => (
              <div key={idx} className={styles.amendRow}>
                <select
                  value={row.fieldId}
                  onChange={e => updateRow(idx, 'fieldId', e.target.value)}
                  className={styles.input}
                  required
                >
                  <option value="">Select field…</option>
                  {fields.map(([key, label, val]) => (
                    <option key={key} value={key}>
                      {label} (current: {toDisplayValue(val)})
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={row.correctedValue}
                  onChange={e => updateRow(idx, 'correctedValue', e.target.value)}
                  className={styles.input}
                  placeholder="New value"
                  required
                />
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  disabled={rows.length === 1}
                  className={styles.btnRemoveAmendRow}
                  title="Remove"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>

          <button type="button" onClick={addRow} className={styles.btnAddAmendRow}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add Field
          </button>

          <label className={styles.label}>
            <span>Reason <span className={styles.required}>*</span></span>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              className={styles.textarea}
              rows={3}
              required
              placeholder="Explain why this amendment is needed..."
            />
          </label>

          <div className={styles.formActions}>
            <button type="button" onClick={onCancel} className={styles.btnSecondary}>Cancel</button>
            <button type="submit" disabled={saving} className={styles.btnPrimary}>
              {saving ? 'Saving…' : `Submit Amendment${rows.length > 1 ? 's' : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Accordion data panel ──────────────────────────────────────────────────────

function AccordionRow({ entry, colSpan, isExpanded }) {
  const parsed = parseEntryData(entry.data_decoded);
  const { isGrid, fieldNames, rows } = parsed;
  const submittedAt = new Date(entry.submitted_at);
  const submittedLabel = `${formatDate(entry.entry_date)} ${submittedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

  return (
    <tr className={styles.accordionTr}>
      <td colSpan={colSpan} className={styles.accordionTd}>
        <div className={`${styles.accordionBody} ${isExpanded ? styles.accordionBodyOpen : ''}`}>
          <div className={styles.accordionInner}>
            {/* Top bar: badge left, submitted-by right */}
            <div className={styles.accordionTopBar}>
              <div>
                {entry.amendments_count > 0 && (
                  <div className={styles.amendedBadge}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                    </svg>
                    {entry.amendments_count} Amendment{entry.amendments_count !== 1 ? 's' : ''} on this entry
                  </div>
                )}
              </div>
              <div className={styles.accordionMeta}>
                <span>Submitted by <strong>{entry.reporter_email}</strong></span>
                <span className={styles.dot}>·</span>
                <span>{submittedLabel}</span>
              </div>
            </div>

            {/* Data display */}
            {isGrid ? (
              <GridDataTable template={parsed.gridTemplate} gridData={parsed.gridData} />
            ) : fieldNames.length > 0 ? (
              <div className={styles.dataTableWrap}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th className={styles.dataTableRowNum}>#</th>
                      {fieldNames.map(name => (
                        <th key={name}>{name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        <td className={styles.dataTableRowNum}>{rowIdx + 1}</td>
                        {fieldNames.map(name => (
                          <td key={name}>{toDisplayValue(row[name])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={styles.noDataText}>No data available for this entry.</p>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const COL_COUNT = 7; // Sl.No · Date · Business · Reporter · Amendments · Submitted · Actions

function Entries() {
  const [entries, setEntries] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ business_id: '', date_from: '', date_to: '' });
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [amendEntryId, setAmendEntryId] = useState(null);
  const [amendEntryData, setAmendEntryData] = useState(null);
  const [viewAmendmentsId, setViewAmendmentsId] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);

  const fetchEntries = useCallback(async () => {
    setPage(1);
    setLoading(true);
    try {
      const params = {};
      if (filters.business_id) params.business_id = filters.business_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await api.get('/entries/', { params });
      if (res.data.success) setEntries(res.data.data || []);
    } catch { /* empty */ }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    async function loadBusinesses() {
      try {
        const res = await api.get('/businesses/');
        if (res.data.success) setBusinesses(res.data.data || []);
      } catch { /* empty */ }
    }
    loadBusinesses();
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  function toggleExpand(id) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  }

  function handleAmend(entry) {
    setAmendEntryId(entry.id);
    setAmendEntryData(entry.data_decoded);
  }

  return (
    <div>
      <PageHeader title="Entries" subtitle="View daily entries and audit trail" />

      {/* Filters */}
      <div className={styles.filters}>
        <select
          value={filters.business_id}
          onChange={e => setFilters({ ...filters, business_id: e.target.value })}
          className={styles.filterInput}
        >
          <option value="">All Businesses</option>
          {businesses.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        <input
          type="date"
          value={filters.date_from}
          onChange={e => setFilters({ ...filters, date_from: e.target.value })}
          className={styles.filterInput}
          placeholder="dd-mm-yyyy"
        />
        <input
          type="date"
          value={filters.date_to}
          onChange={e => setFilters({ ...filters, date_to: e.target.value })}
          className={styles.filterInput}
          placeholder="dd-mm-yyyy"
        />
      </div>

      {loading ? (
        <p className={styles.loadingText}>Loading...</p>
      ) : entries.length === 0 ? (
        <div className={styles.emptyState}>
          <h3>No entries found</h3>
          <p>Entries will appear here once reporters begin submitting daily data.</p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <colgroup>
              <col className={styles.colSlNo} />
              <col className={styles.colDate} />
              <col className={styles.colBusiness} />
              <col className={styles.colReporter} />
              <col className={styles.colAmend} />
              <col className={styles.colSubmitted} />
              <col className={styles.colActions} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ textAlign: 'center' }}>#</th>
                <th>Date</th>
                <th>Business</th>
                <th>Reporter</th>
                <th>Amendments</th>
                <th>Time</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.slice((page - 1) * perPage, page * perPage).map((entry, idx) => {
                const isExpanded = expandedIds.has(entry.id);
                return (
                  <React.Fragment key={entry.id}>
                    {/* Main row */}
                    <tr className={isExpanded ? styles.rowExpanded : ''}>
                      <td style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                        {(page - 1) * perPage + idx + 1}
                      </td>
                      <td className={styles.dateCell}>{formatDate(entry.entry_date)}</td>
                      <td className={styles.bizCell} title={entry.business_name || ''}>{entry.business_name || '—'}</td>
                      <td className={styles.reporterCell} title={entry.reporter_email}>{entry.reporter_email}</td>
                      <td>
                        {entry.amendments_count > 0 ? (
                          <button
                            onClick={() => setViewAmendmentsId(entry.id)}
                            className={styles.amendmentCount}
                          >
                            {entry.amendments_count} amend{entry.amendments_count !== 1 ? 's' : ''}
                          </button>
                        ) : (
                          <span className={styles.noAmendments}>—</span>
                        )}
                      </td>
                      <td className={styles.timeCell}>
                        {new Date(entry.submitted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td>
                        <div className={styles.actions}>
                          {/* Expand / collapse data */}
                          <button
                            onClick={() => toggleExpand(entry.id)}
                            className={`${styles.iconBtn} ${styles.iconBtnExpand} ${isExpanded ? styles.iconBtnExpandActive : ''}`}
                            title={isExpanded ? 'Collapse data' : 'View entry data'}
                          >
                            <svg
                              width="14" height="14" viewBox="0 0 24 24"
                              fill="none" stroke="currentColor" strokeWidth="2.5"
                              strokeLinecap="round" strokeLinejoin="round"
                              className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </button>
                          {/* Amend */}
                          <button
                            onClick={() => handleAmend(entry)}
                            className={`${styles.iconBtn} ${styles.iconBtnAmend}`}
                            title="Amend entry"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 20h9"/>
                              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                            </svg>
                          </button>
                          {/* History */}
                          <button
                            onClick={() => setViewAmendmentsId(entry.id)}
                            className={`${styles.iconBtn} ${styles.iconBtnHistory}`}
                            title="Amendment history"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/>
                              <polyline points="12 6 12 12 16 14"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Accordion row — always in DOM, animated via max-height */}
                    <AccordionRow
                      entry={entry}
                      colSpan={COL_COUNT}
                      isExpanded={isExpanded}
                    />
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          <Pagination
            total={entries.length}
            page={page}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={n => { setPerPage(n); setPage(1); }}
          />
        </div>
      )}

      {viewAmendmentsId && (
        <AmendmentPanel
          entryId={viewAmendmentsId}
          onClose={() => setViewAmendmentsId(null)}
        />
      )}

      {amendEntryId && (
        <AmendForm
          entryId={amendEntryId}
          entryData={amendEntryData}
          onSave={() => { setAmendEntryId(null); fetchEntries(); }}
          onCancel={() => setAmendEntryId(null)}
        />
      )}
    </div>
  );
}

export default Entries;
